import { Link } from "@tanstack/react-router";
import { clsx } from "clsx";
import {
  ArrowRight,
  ChevronRight,
  Percent,
  Receipt,
  Settings,
  Shield,
  TrendingUp,
  Users,
} from "lucide-react";
import { PERMISSIONS, userHasPermission } from "@/auth/permissions";
import { Card } from "@/components/common/Card";
import { Spinner } from "@/components/common/Spinner";
import { PageHeader } from "@/components/layout/PageHeader";
import { StudioDashboard } from "@/components/studio/StudioDashboard";
import { useUser } from "@/hooks/useAuth";
import {
  useAgentDashboardStats,
  useDashboardStats,
} from "@/hooks/useDashboard";
import { useInterfaceLayout } from "@/hooks/useInterfaceLayout";
import { useHasPermission } from "@/hooks/usePermission";
import { useCompanyResults } from "@/hooks/useReports";

function getGreeting(): string {
  const hour = new Date().getHours();
  return hour >= 6 && hour <= 17 ? "Dzień dobry" : "Dobry wieczór";
}

function formatDate(): string {
  return new Date().toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
  }).format(value);
}

function formatPayoutDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ============================
// Stat strip
// ============================

interface Stat {
  label: string;
  value: string;
  hint?: string;
}

// One bordered strip with thin dividers instead of a card per number.
function StatStrip({ stats }: { stats: Stat[] }) {
  return (
    <Card padding="none">
      <dl
        className={clsx(
          "grid divide-y divide-edge-soft sm:divide-x sm:divide-y-0",
          stats.length === 4
            ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
            : "grid-cols-1 sm:grid-cols-3",
        )}
      >
        {stats.map((stat) => (
          <div key={stat.label} className="px-5 py-4">
            <dt className="text-[13px] text-dim">{stat.label}</dt>
            <dd className="mt-1.5 text-2xl font-semibold tracking-tight text-main tabular-nums">
              {stat.value}
            </dd>
            {stat.hint && (
              <dd className="mt-1 text-[12.5px] text-dim">{stat.hint}</dd>
            )}
          </div>
        ))}
      </dl>
    </Card>
  );
}

function StatsLoading() {
  return (
    <div className="flex justify-center py-12">
      <Spinner size="lg" />
    </div>
  );
}

// ============================
// Quick actions
// ============================

interface QuickAction {
  label: string;
  description: string;
  href: string;
  icon: typeof Users;
  permission?: string;
}

const adminQuickActions: QuickAction[] = [
  {
    label: "Wyniki firmy",
    description: "Przychód, wypłaty agentom i marża",
    href: "/results",
    icon: TrendingUp,
    permission: PERMISSIONS.REPORTS.READ,
  },
  {
    label: "Agenci",
    description: "Konta, upoważnienia w towarzystwach i egzaminy",
    href: "/agents",
    icon: Users,
    permission: PERMISSIONS.USERS.READ,
  },
  {
    label: "Role",
    description: "Konfiguruj role i uprawnienia",
    href: "/roles",
    icon: Shield,
    permission: PERMISSIONS.ROLES.READ,
  },
  {
    label: "Konfiguracja prowizji",
    description: "Ustawienia stawek prowizyjnych",
    href: "/commissions",
    icon: Percent,
    permission: PERMISSIONS.SETTLEMENTS.UPDATE,
  },
  {
    label: "Ustawienia",
    description: "Ustawienia systemu",
    href: "/settings",
    icon: Settings,
  },
];

const agentQuickActions: QuickAction[] = [
  {
    label: "Wyniki firmy",
    description: "Przychód, wypłaty agentom i marża",
    href: "/results",
    icon: TrendingUp,
    permission: PERMISSIONS.REPORTS.READ,
  },
  {
    label: "Rozliczenia",
    description: "Przegląd Twoich rozliczeń",
    href: "/settlements",
    icon: Receipt,
    permission: PERMISSIONS.SETTLEMENTS.READ,
  },
  {
    label: "Ustawienia",
    description: "Ustawienia konta",
    href: "/settings",
    icon: Settings,
  },
];

function QuickActions({ actions }: { actions: QuickAction[] }) {
  return (
    <Card padding="none">
      <h2 className="border-b border-edge-soft px-5 py-3.5 text-sm font-semibold text-main">
        Szybkie akcje
      </h2>
      {/* gap-px over a tinted background draws the dividers between grid cells */}
      <div className="grid gap-px overflow-hidden rounded-b-lg bg-edge-soft sm:grid-cols-2">
        {actions.map((action) => (
          <Link
            key={action.href}
            to={action.href}
            className="group flex items-center gap-3.5 bg-panel px-5 py-3.5 transition-colors hover:bg-subtle sm:last:odd:col-span-2"
          >
            <action.icon
              size={18}
              strokeWidth={1.75}
              className="shrink-0 text-dim"
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-main">{action.label}</p>
              <p className="text-[13px] text-dim">{action.description}</p>
            </div>
            <ChevronRight
              size={16}
              className="shrink-0 text-faint transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </Link>
        ))}
      </div>
    </Card>
  );
}

function useVisibleActions(isAdmin: boolean) {
  const canUsers = useHasPermission(PERMISSIONS.USERS.READ);
  const canRoles = useHasPermission(PERMISSIONS.ROLES.READ);
  const canCommissions = useHasPermission(PERMISSIONS.SETTLEMENTS.UPDATE);
  const canSettlements = useHasPermission(PERMISSIONS.SETTLEMENTS.READ);
  const canReports = useHasPermission(PERMISSIONS.REPORTS.READ);

  const permMap: Record<string, boolean> = {
    [PERMISSIONS.USERS.READ]: canUsers,
    [PERMISSIONS.ROLES.READ]: canRoles,
    [PERMISSIONS.SETTLEMENTS.UPDATE]: canCommissions,
    [PERMISSIONS.SETTLEMENTS.READ]: canSettlements,
    [PERMISSIONS.REPORTS.READ]: canReports,
  };

  const actions = isAdmin ? adminQuickActions : agentQuickActions;
  return actions.filter((a) => !a.permission || permMap[a.permission]);
}

// ============================
// Admin stats
// ============================

function AdminStats() {
  const { data: stats, isLoading } = useDashboardStats();

  if (isLoading) {
    return <StatsLoading />;
  }

  if (!stats) {
    return null;
  }

  return (
    <StatStrip
      stats={[
        { label: "Agenci", value: String(stats.totalUsers) },
        {
          label: "Aktywni agenci",
          value: String(stats.activeAgents),
          hint: `z ${stats.totalUsers} kont`,
        },
        {
          label: "Domyślna prowizja",
          value: `${stats.defaultCommissionRate}%`,
        },
        {
          label: "Niestandardowe stawki",
          value: String(stats.customCommissionCount),
        },
      ]}
    />
  );
}

// ============================
// Agent stats
// ============================

function AgentStats() {
  const { data: stats, isLoading } = useAgentDashboardStats();

  if (isLoading) {
    return <StatsLoading />;
  }

  if (!stats) {
    return null;
  }

  return (
    <StatStrip
      stats={[
        {
          label: `Wypłaty w ${new Date().getFullYear()}`,
          value: formatCurrency(stats.totalPayoutsThisYear),
        },
        {
          label: "Ostatnia wypłata",
          value:
            stats.lastPayoutAmount !== null
              ? formatCurrency(stats.lastPayoutAmount)
              : "—",
        },
        {
          label: "Data ostatniej wypłaty",
          value: stats.lastPayoutDate
            ? formatPayoutDate(stats.lastPayoutDate)
            : "—",
        },
      ]}
    />
  );
}

// ============================
// Company results (partner view)
// ============================

function CompanyResultsSummary() {
  const now = new Date();
  const { data, isLoading } = useCompanyResults(
    now.getFullYear(),
    now.getMonth() + 1,
  );

  if (isLoading) {
    return <StatsLoading />;
  }

  // The current month is usually not imported yet — show the latest month with settlements.
  const latest =
    data && [...data.trend].reverse().find((m) => m.agentCount > 0);
  if (!latest) {
    return null;
  }

  return (
    <div className="space-y-2">
      <StatStrip
        stats={[
          {
            label: "Przychód z prowizji",
            value: formatCurrency(latest.revenue),
          },
          {
            label: "Wypłaty agentom",
            value: formatCurrency(latest.agentPayouts),
          },
          { label: "Marża firmy", value: formatCurrency(latest.margin) },
        ]}
      />
      <Link
        to="/results"
        className="inline-flex items-center gap-1 text-sm font-medium text-adara-700 hover:underline dark:text-adara-300"
      >
        {new Date(latest.year, latest.month - 1).toLocaleDateString("pl-PL", {
          month: "long",
          year: "numeric",
        })}{" "}
        · zobacz wyniki firmy
        <ArrowRight size={14} aria-hidden />
      </Link>
    </div>
  );
}

// ============================
// Main Page
// ============================

export function DashboardPage() {
  const user = useUser();
  const isAdmin = user?.role === "Administrator";
  // The partner role sees the company's results, not an agent's own payouts.
  const isPartner =
    !isAdmin &&
    userHasPermission(user, PERMISSIONS.REPORTS.READ) &&
    !userHasPermission(user, PERMISSIONS.SETTLEMENTS.READ);
  const visibleActions = useVisibleActions(isAdmin);
  const { layout } = useInterfaceLayout();
  if (layout === "studio") {
    return (
      <StudioDashboard
        summary={
          isAdmin ? (
            <AdminStats />
          ) : isPartner ? (
            <CompanyResultsSummary />
          ) : userHasPermission(user, PERMISSIONS.SETTLEMENTS.READ) ? (
            <AgentStats />
          ) : null
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`${getGreeting()}, ${user?.firstName ?? "Użytkowniku"}`}
        description={`${formatDate()} · ${user?.role ?? ""}`}
      />

      {isAdmin ? (
        <AdminStats />
      ) : isPartner ? (
        <CompanyResultsSummary />
      ) : (
        <AgentStats />
      )}

      {visibleActions.length > 0 && <QuickActions actions={visibleActions} />}
    </div>
  );
}
